/**
 * CT product attributes allowed in the shop-category attribute criteria dropdown.
 * Sourced from the Autoshop PUMA allowlist (mapped to CT `name`s + `ctType`s, incl. regional
 * variants and agreed companions). New CT fields stay hidden from the PUMA Shop Category
 * Definitions page until explicitly added here.
 *
 * Shared by merchant-util-api (dropdown filter) and shop-criteria-worker (predicates).
 */
export type ShopCriteriaAttributeCtType = 'text' | 'ltext' | 'set<text>' | 'set<ltext>' | 'boolean' | 'number';

export type ShopCriteriaAttributeAllowlistEntry = {
    name: string;
    ctType: ShopCriteriaAttributeCtType;
    /** Optional dropdown label; when omitted, the CT product-type label is used. */
    label?: string;
};

export const SHOP_CRITERIA_ATTRIBUTE_ALLOWLIST: ReadonlyArray<ShopCriteriaAttributeAllowlistEntry> = [
    { name: 'averageLifespans', ctType: 'set<ltext>' },
    { name: 'bed-features', ctType: 'set<ltext>' },
    { name: 'bed-sizeDisplays', ctType: 'set<ltext>' },
    { name: 'bed-types', ctType: 'set<ltext>' },
    { name: 'birdCageDisplays', ctType: 'set<ltext>' },
    { name: 'birdFoodTypes', ctType: 'set<ltext>' },
    { name: 'brand', ctType: 'text' },
    { name: 'bulbType', ctType: 'ltext' },
    { name: 'bvAverageRating', ctType: 'number' },
    { name: 'bvReviewCount', ctType: 'number' },
    { name: 'catLifestages', ctType: 'set<text>' },
    { name: 'catWeightRange', ctType: 'ltext' },
    { name: 'catWeightRangeDisplays', ctType: 'set<ltext>' },
    { name: 'climateControlDevices', ctType: 'set<ltext>' },
    { name: 'coatTypes', ctType: 'set<ltext>' },
    { name: 'collarAndLeashSizeDisplays', ctType: 'set<ltext>' },
    { name: 'collection-ca', ctType: 'set<text>' },
    { name: 'collection-us', ctType: 'set<text>' },
    { name: 'color-families', ctType: 'set<ltext>' },
    { name: 'color-swatchDisplay', ctType: 'ltext' },
    { name: 'color-value', ctType: 'ltext' },
    { name: 'costumeTypes', ctType: 'set<ltext>' },
    { name: 'crateAndCarrier-sizeDisplays', ctType: 'set<ltext>' },
    { name: 'crateAndCarrier-types', ctType: 'set<ltext>' },
    { name: 'crateMatsAndPadsSizeDisplays', ctType: 'set<ltext>' },
    { name: 'customCategoryList', ctType: 'set<text>', label: 'Category' },
    { name: 'customPharmacyColdStorage', ctType: 'boolean' },
    { name: 'customPharmacyOOS', ctType: 'boolean' },
    { name: 'customScheduledDeliveryEligible-ca', ctType: 'boolean' },
    { name: 'customScheduledDeliveryEligible-us', ctType: 'boolean' },
    { name: 'designThemes', ctType: 'set<ltext>' },
    { name: 'dimensionRanges', ctType: 'set<ltext>' },
    { name: 'dogAccessories', ctType: 'ltext' },
    { name: 'dogLifestages', ctType: 'set<text>' },
    { name: 'dogWeightRangeDisplays', ctType: 'set<ltext>' },
    { name: 'doorAndGateTypes', ctType: 'set<ltext>' },
    { name: 'environments', ctType: 'set<ltext>' },
    { name: 'experienceLevels', ctType: 'set<ltext>' },
    { name: 'fenceRanges', ctType: 'set<ltext>' },
    { name: 'fenceSystemTypes', ctType: 'set<ltext>' },
    { name: 'filterType', ctType: 'ltext' },
    { name: 'fishFeederTypes', ctType: 'set<ltext>' },
    { name: 'fishFoodTypes', ctType: 'set<ltext>' },
    { name: 'flavor-displays', ctType: 'set<ltext>' },
    { name: 'flavor-value', ctType: 'ltext' },
    { name: 'foodCategory', ctType: 'ltext' },
    { name: 'foodForms', ctType: 'set<ltext>' },
    { name: 'foodOuncesSizeDisplays', ctType: 'set<ltext>' },
    { name: 'foodPoundsSizeDisplays', ctType: 'set<ltext>' },
    { name: 'foodStorageCapacity', ctType: 'ltext' },
    { name: 'foodTextures', ctType: 'set<ltext>' },
    { name: 'furnitureHeight', ctType: 'ltext' },
    { name: 'gateSizes', ctType: 'set<ltext>' },
    { name: 'genders', ctType: 'set<ltext>' },
    { name: 'groomingTypes', ctType: 'set<ltext>' },
    { name: 'habitat-accessories', ctType: 'set<ltext>' },
    { name: 'habitat-decorTypes', ctType: 'set<ltext>' },
    { name: 'healthConsiderations', ctType: 'set<ltext>' },
    { name: 'intendedProductUses', ctType: 'set<ltext>' },
    { name: 'isLivePet', ctType: 'boolean' },
    { name: 'isSubscriptionEnabled-ca', ctType: 'boolean' },
    { name: 'isSubscriptionEnabled-us', ctType: 'boolean' },
    { name: 'kibbleSizes', ctType: 'set<ltext>' },
    { name: 'leashTypes', ctType: 'set<ltext>' },
    { name: 'litter-features', ctType: 'set<ltext>' },
    { name: 'litter-materials', ctType: 'set<ltext>' },
    { name: 'litter-types', ctType: 'set<ltext>' },
    { name: 'litterBoxType', ctType: 'ltext' },
    { name: 'materials', ctType: 'set<ltext>' },
    { name: 'maturitySizes', ctType: 'set<ltext>' },
    { name: 'moreWaysToShop-ca', ctType: 'set<text>' },
    { name: 'moreWaysToShop-us', ctType: 'set<text>' },
    { name: 'nutritionalOptions', ctType: 'set<ltext>' },
    { name: 'packagingTypes', ctType: 'set<ltext>' },
    { name: 'petSizeDisplays', ctType: 'set<ltext>' },
    { name: 'petType-amphibians', ctType: 'set<ltext>' },
    { name: 'petType-birds', ctType: 'set<ltext>' },
    { name: 'petType-breeds', ctType: 'set<ltext>' },
    { name: 'petType-fishSpecies', ctType: 'set<ltext>' },
    { name: 'petType-pets', ctType: 'set<text>' },
    { name: 'petType-reptiles', ctType: 'set<ltext>' },
    { name: 'petType-smallPets', ctType: 'set<ltext>' },
    { name: 'pharmacy-rxStrength', ctType: 'ltext' },
    { name: 'pharmacy-sku', ctType: 'ltext' },
    { name: 'pharmacy-type', ctType: 'text' },
    { name: 'productApplicationMethods', ctType: 'set<ltext>' },
    { name: 'productLifetime', ctType: 'ltext' },
    { name: 'promoOfferName', ctType: 'set<text>' },
    { name: 'quantity', ctType: 'ltext' },
    { name: 'quantityDisplays', ctType: 'set<ltext>' },
    { name: 'reptileFoodType', ctType: 'ltext' },
    { name: 'scent', ctType: 'ltext' },
    { name: 'series', ctType: 'set<text>' },
    { name: 'shapes', ctType: 'set<ltext>' },
    { name: 'shipToHome-isCaseRequired-ca', ctType: 'boolean' },
    { name: 'shipToHome-isCaseRequired-us', ctType: 'boolean' },
    { name: 'shipToHome-isCASupported', ctType: 'boolean' },
    { name: 'shoppingOptions-isInStorePickUp', ctType: 'boolean' },
    { name: 'shoppingOptions-isSoldOnlineOnly', ctType: 'boolean' },
    { name: 'size-fluidSize', ctType: 'ltext' },
    { name: 'size-petSize', ctType: 'set<ltext>' },
    { name: 'size-solidSize', ctType: 'ltext' },
    { name: 'size-threeDimensionalSize', ctType: 'ltext' },
    { name: 'size-twoDimensionalSize', ctType: 'ltext' },
    { name: 'smallPetAccessoryTypes', ctType: 'set<ltext>' },
    { name: 'sports-league', ctType: 'ltext' },
    { name: 'sports-team', ctType: 'ltext' },
    { name: 'stainTypes', ctType: 'set<ltext>' },
    { name: 'standType', ctType: 'ltext' },
    { name: 'tank-sizes', ctType: 'set<text>' },
    { name: 'toyFeatures', ctType: 'set<ltext>' },
    { name: 'treatTypes', ctType: 'set<ltext>' },
    { name: 'vetAuthorizedDiets', ctType: 'set<ltext>' },
    { name: 'waterTreatment', ctType: 'ltext' },
    { name: 'waterTypes', ctType: 'set<ltext>' },
    { name: 'wattage', ctType: 'ltext' },
    { name: 'wattageRanges', ctType: 'set<ltext>' },
    { name: 'primaryCategoryId', ctType: 'text', label: 'Category External ID' },
];

/** Name lookup for dropdown allowlist filtering. */
export const SHOP_CRITERIA_ATTRIBUTE_ALLOWLIST_NAMES: ReadonlySet<string> = new Set(
    SHOP_CRITERIA_ATTRIBUTE_ALLOWLIST.map(({ name }) => name)
);

/** ctType lookup for shop-criteria where builders. */
export const SHOP_CRITERIA_ATTRIBUTE_CT_TYPE_BY_NAME: ReadonlyMap<string, ShopCriteriaAttributeCtType> = new Map(
    SHOP_CRITERIA_ATTRIBUTE_ALLOWLIST.map(({ name, ctType }) => [name, ctType])
);

/** Optional dropdown label overrides from the allowlist. */
export const SHOP_CRITERIA_ATTRIBUTE_LABEL_BY_NAME: ReadonlyMap<string, string> = new Map(
    SHOP_CRITERIA_ATTRIBUTE_ALLOWLIST.flatMap(({ name, label }) =>
        label === undefined ? [] : [[name, label] as const]
    )
);
